import { get, isEqual, difference, trim } from 'lodash';
import { createMarkdownBlock } from 'src/app/content-blocks/models/content-block';
import { HitModel } from 'src/app/engine/models/hit.model';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { uuid } from 'src/app/shared/uuid';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { LabelPinType } from 'src/app/story/models/labels/label-pin-type.enum';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { QuestionSubmission } from '../models/question-submission.model';
import { QuizQuestionType } from '../models/quiz-question-type.enum';
import {
  LabelQuestion,
  MultipleChoiceQuestion,
  QuizLabelOption,
  QuizOption,
  QuizQuestion,
  QuizQuestionUnion,
  QuizScoreModel,
  SelectAnatomyQuestion,
  InstructionsQuestion,
} from '../models/quiz-question.model';

export const defaultNextButtonNext = 'Next Question';

export function createQuestionsFromChapters(chapters: ChapterModel[]): QuizQuestion[] {
  const quizQuestions = chapters.map((chapter) => {
    const questions = chapter.quizQuestions || [];
    return questions.map((question) => {
      return {
        ...question,
        // ensure chapter id
        chapterId: question.chapterId || chapter.id,
      };
    });
  });
  return [].concat(...quizQuestions);
}

export function createQuizQuestion(type: QuizQuestionType): QuizQuestionUnion {
  // create base question
  const question: QuizQuestion = {
    id: uuid.idWithPrefix('question'),
    chapterId: '',
    type: type,
    questionPrompt: [createMarkdownBlock('')],
    data: {},
    explanation: [createMarkdownBlock('')],
    nextLabel: '',
    showChapterLabels: false,
    sceneConfig: {},
  };

  // return type specific question
  switch (type) {
    case QuizQuestionType.MultipleChoice:
      const options = [createQuizOption(), createQuizOption()];
      return <MultipleChoiceQuestion>{
        ...question,
        data: {
          // set default selection
          correctAnswer: options[0].value,
          // default options
          options: options,
        },
      };

    case QuizQuestionType.SelectAnatomy:
      return <SelectAnatomyQuestion>{
        ...question,
        data: {
          showAnswerList: true,
          selectType: 'all',
          correctAnswer: [],
        },
      };
    case QuizQuestionType.LabelAnatomy:
      return <LabelQuestion>{
        ...question,
        data: {
          correctAnswer: [],
          options: [],
        },
      };
    case QuizQuestionType.Instructions:
      return <InstructionsQuestion>{
        ...question,
      };
  }
}

export function createQuizOption(label: string = ''): QuizOption {
  return {
    value: uuid.idWithPrefix('question-option'),
    label: label,
  };
}

export function createLabelQuizOption(label: LabelModel): QuizLabelOption {
  return {
    value: uuid.idWithPrefix('question-option'),
    label: label,
  };
}

export function createQuizLabel(node: ViewTreeNode, hit: HitModel, direction: any): LabelModel {
  const label = new LabelModel(node.label);
  // unique id
  label.id = uuid.idWithPrefix('quiz-label');
  const tagDistance = label.hideWire ? 20 : 32;
  label.title = trim(node.label); // Node labels sometimes include errant trailing whitespace
  label.offset = [tagDistance, 0];
  label.objectId = hit.objectId;
  label.barycentric = [hit.barycentric[0], hit.barycentric[1], hit.barycentric[2]];
  label.pos = [hit.worldPos[0], hit.worldPos[1], hit.worldPos[2]];
  label.primitiveIndex = hit.primitiveIndex;
  label.renderableIndex = hit.renderableIndex || 0;
  label.dir = direction;
  label.occludable = true;
  label.shown = true;
  label.pinType = LabelPinType.Sequential;

  return label;
}

export function isAnswerCorrect(question: QuizQuestion, answer: string[]): boolean {
  const correctAnswer: string[] =
    typeof question.data.correctAnswer === 'string'
      ? [question.data.correctAnswer]
      : question.data.correctAnswer;
  switch (question.type) {
    // compare ordered responses
    case QuizQuestionType.LabelAnatomy:
      return isEqual(correctAnswer, answer);
    // compare un-ordered responses
    case QuizQuestionType.MultipleChoice:
      return isEqual(correctAnswer.sort(), answer.sort());
    case QuizQuestionType.SelectAnatomy:
      // Early quizzes do not contain selectType, this is the same as selectType === 'all'
      if (question.data.selectType === undefined || question.data.selectType === 'all') {
        return isEqual(correctAnswer.sort(), answer.sort());
      } else if (question.data.selectType === 'any') {
        return difference(answer, correctAnswer).length === 0 && answer.length > 0;
      }
  }
}

export function findQuestionAnswers(
  submissions: QuestionSubmission[],
  questionId: string,
): string | string[] | undefined {
  const submission = submissions.find((value) => value.questionId === questionId);
  return submission ? submission.answer : undefined;
}

export function scoreQuiz(
  questions: QuizQuestion[],
  submissions: QuestionSubmission[],
): QuizScoreModel {
  // scorable questions
  const scoreableQuestions = questions.filter(
    (question) => question.type !== QuizQuestionType.Instructions,
  );

  // map of all questions to answers
  const questionAnswers = questions.reduce((acc, question) => {
    acc[question.id] = findQuestionAnswers(submissions, question.id);
    return acc;
  }, {});

  // get correct questions: only scorable
  const correctQuestions = scoreableQuestions.filter((question) => {
    const answer = questionAnswers[question.id];
    const answers =
      typeof answer === 'undefined' ? [] : typeof answer === 'string' ? [answer] : answer;
    return isAnswerCorrect(question, answers);
  });

  const total = questions.length;
  const scorableTotal = scoreableQuestions.length;
  const correctTotal = correctQuestions.length;
  const incorrectTotal = total - correctTotal;
  const score = total > 0 ? Math.round((correctTotal / scorableTotal) * 100) : 0;

  return {
    score,
    total,
    scorableTotal,
    correctTotal,
    incorrectTotal,
    questionAnswers,
  };
}

// checks if the answers are valid (i.e., not empty)
export function hasValidAnswers(question: QuizQuestion): boolean {
  const type = get(question, 'type', '');

  switch (type) {
    case QuizQuestionType.MultipleChoice:
      const correctChoice: string = get(question, 'data.correctAnswer', '');
      return correctChoice && correctChoice !== '';
    case QuizQuestionType.SelectAnatomy:
      const correctAnatomy: string[] = get(question, 'data.correctAnswer', []);
      return correctAnatomy.length > 0;
    case QuizQuestionType.LabelAnatomy:
      const correctLabels: string[] = get(question, 'data.correctAnswer', []);
      return correctLabels.length > 0;
    default:
      return true;
  }
}
